import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, SpinnerWithBackground, Text } from '@viapeople/components'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { sessionStore } from '../../../sharedStores/sessionStore'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import { useEnpsStore } from '../store'

/**
 * @fileoverview eNPS Launch Tab
 *
 * Launch surveys and manage active executions
 */

export const EnpsLaunchTab = () => {
	const { enpsId } = useParams<{ enpsId: string }>()
	const { user } = sessionStore()
	const organizationId = user?.organizationId
	const { enpsConfig, useActivateEnpsConfig, usePauseEnpsConfig } = useEnpsStore()
	const { activateEnpsConfig, isLoading: isActivating } = useActivateEnpsConfig(enpsId as string)
	const { pauseEnpsConfig, isLoading: isPausing } = usePauseEnpsConfig(enpsId as string)
	const toast = useToast()
	const queryClient = useQueryClient()
	const [isLaunching, setIsLaunching] = useState(false)
	const [isClosing, setIsClosing] = useState(false)

	// Fetch active execution
	const { data: activeExecution, isLoading: isLoadingExecution } = useQuery({
		...trpc.client.enps.getActiveExecution.queryOptions({
			enpsConfigId: enpsId as string,
		}),
		enabled: !!enpsId,
	})

	// Fetch results for response status reporting
	const { data: results } = useQuery({
		...trpc.client.enps.getConfigResults.queryOptions({
			enpsConfigId: enpsId as string,
			organizationId: organizationId as string,
		}),
		enabled: !!enpsId && !!organizationId,
		refetchInterval: enpsConfig?.status === 'active' ? 30000 : false,
	})

	const launchMutation = useMutation({
		...trpc.client.enps.launch.mutationOptions(),
		onSuccess: () => {
			toast.showToast('NPS survey launched successfully')
			setIsLaunching(false)
			queryClient.invalidateQueries({
				queryKey: trpc.client.enps.getActiveExecution.queryKey(),
			})
			queryClient.invalidateQueries({
				queryKey: trpc.client.enps.getConfigResults.queryKey(),
			})
		},
		onError: (error) => {
			toast.showToast(error.message || 'Failed to launch survey', 'error')
			setIsLaunching(false)
		},
	})

	const closeExecutionMutation = useMutation({
		...trpc.client.enps.closeExecution.mutationOptions(),
		onSuccess: () => {
			toast.showToast('Survey execution closed successfully')
			setIsClosing(false)
			queryClient.invalidateQueries({
				queryKey: trpc.client.enps.getActiveExecution.queryKey(),
			})
			queryClient.invalidateQueries({
				queryKey: trpc.client.enps.getById.queryKey(),
			})
			queryClient.invalidateQueries({
				queryKey: trpc.client.enps.getConfigResults.queryKey(),
			})
		},
		onError: (error) => {
			toast.showToast(error.message || 'Failed to close execution', 'error')
			setIsClosing(false)
		},
	})

	const handleLaunch = async () => {
		if (!enpsConfig || !organizationId || !enpsId) return

		if (!enpsConfig.populationId) {
			toast.showToast('Please configure a population first', 'error')
			return
		}

		if (!enpsConfig.emailTemplateId) {
			toast.showToast('Please select an email template first', 'error')
			return
		}

		setIsLaunching(true)
		await launchMutation.mutateAsync({
			enpsConfigId: enpsId,
			organizationId,
			emailTemplateId: enpsConfig.emailTemplateId,
		})
	}

	const handleCloseExecution = async () => {
		if (!activeExecution || !organizationId) return

		setIsClosing(true)
		await closeExecutionMutation.mutateAsync({
			executionId: activeExecution.id,
			organizationId,
		})
	}

	const handleActivate = async () => {
		await activateEnpsConfig()
	}

	const handlePause = async () => {
		await pauseEnpsConfig()
	}

	const isPending = isLaunching || isActivating || isPausing || isClosing || isLoadingExecution

	const formatStatus = (status: string) => {
		return status.charAt(0).toUpperCase() + status.slice(1)
	}

	return (
		<div className='relative pb-12 mt-6 w-full'>
			{isPending && <SpinnerWithBackground />}
			<div className='px-6 max-w-4xl space-y-6'>
				<div>
					<Text variant='large' weight='semibold'>
						Survey Status
					</Text>
					<div className='mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4'>
						<div className='flex items-center justify-between'>
							<div>
								<Text variant='small' weight='semibold'>
									Current Status
								</Text>
								<Text variant='small' className='text-neutral-600 mt-1'>
									{enpsConfig?.status ? formatStatus(enpsConfig.status) : 'Unknown'}
								</Text>
							</div>
						</div>
					</div>
				</div>

				{/* Pre-Launch Checklist */}
				<div>
					<Text variant='large' weight='semibold' className='mb-4'>
						Pre-Launch Checklist
					</Text>
					<div className='space-y-2'>
						<div className='flex items-center gap-2'>
							<div className={`w-4 h-4 rounded-full ${enpsConfig?.name ? 'bg-green-500' : 'bg-gray-300'}`} />
							<Text variant='small'>Survey name configured</Text>
						</div>
						<div className='flex items-center gap-2'>
							<div className={`w-4 h-4 rounded-full ${enpsConfig?.questionText ? 'bg-green-500' : 'bg-gray-300'}`} />
							<Text variant='small'>Survey question configured</Text>
						</div>
						<div className='flex items-center gap-2'>
							<div className={`w-4 h-4 rounded-full ${enpsConfig?.populationId ? 'bg-green-500' : 'bg-gray-300'}`} />
							<Text variant='small'>Population selected</Text>
						</div>
						<div className='flex items-center gap-2'>
							<div className={`w-4 h-4 rounded-full ${enpsConfig?.emailTemplateId ? 'bg-green-500' : 'bg-gray-300'}`} />
							<Text variant='small'>Email template selected</Text>
						</div>
					</div>
				</div>

				{/* Active Execution Section */}
				{activeExecution && (
					<div>
						<Text variant='medium' weight='semibold'>
							Active Execution
						</Text>
						<div className='mt-4 bg-green-50 border border-green-200 rounded-lg p-4'>
							<div className='grid grid-cols-2 gap-4'>
								<div>
									<Text variant='xsmall' className='text-gray-600'>
										Launched At
									</Text>
									<Text variant='small' weight='semibold' className='mt-1'>
										{new Date(activeExecution.launchedAt).toLocaleString()}
									</Text>
								</div>
								<div>
									<Text variant='xsmall' className='text-gray-600'>
										Status
									</Text>
									<Text variant='small' weight='semibold' className='mt-1'>
										{activeExecution.status.toUpperCase()}
									</Text>
								</div>
							</div>
							<Button variant='destructive' onClick={handleCloseExecution} className='mt-4' disabled={isClosing}>
								{isClosing ? 'Closing...' : 'Close Execution'}
							</Button>
						</div>
					</div>
				)}

				{/* Response Status */}
				{results && (
					<div>
						<Text variant='large' weight='semibold' className='mb-4'>
							Response Status
						</Text>
						<div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
							<div className='bg-gray-50 border border-gray-200 rounded-lg p-4 text-center'>
								<Text variant='xsmall' className='text-neutral-500'>
									Total Responses
								</Text>
								<Text variant='large' weight='semibold' className='mt-1'>
									{results.score.totalResponses}
								</Text>
							</div>
							<div className='bg-green-50 border border-green-200 rounded-lg p-4 text-center'>
								<Text variant='xsmall' className='text-green-700'>
									Response Rate
								</Text>
								<Text variant='large' weight='semibold' className='mt-1 text-green-700'>
									{results.score.responseRate.toFixed(1)}%
								</Text>
							</div>
							<div className='bg-blue-50 border border-blue-200 rounded-lg p-4 text-center'>
								<Text variant='xsmall' className='text-blue-700'>
									NPS Score
								</Text>
								<Text variant='large' weight='semibold' className='mt-1 text-blue-700'>
									{results.score.npsScore}
								</Text>
							</div>
						</div>

						{/* Response breakdown */}
						<div className='grid grid-cols-3 gap-4 mt-4'>
							<div>
								<div className='flex justify-between items-center mb-1'>
									<Text variant='xsmall' className='text-green-700'>
										Promoters (9-10)
									</Text>
									<Text variant='xsmall' weight='semibold' className='text-green-700'>
										{results.score.promotersPercentage.toFixed(1)}%
									</Text>
								</div>
								<div className='bg-gray-200 rounded-full h-2'>
									<div
										className='bg-green-500 h-2 rounded-full transition-all'
										style={{ width: `${results.score.promotersPercentage}%` }}
									/>
								</div>
								<Text variant='xsmall' className='text-gray-600 mt-1'>
									{results.score.promotersCount} responses
								</Text>
							</div>
							<div>
								<div className='flex justify-between items-center mb-1'>
									<Text variant='xsmall' className='text-yellow-700'>
										Passives (7-8)
									</Text>
									<Text variant='xsmall' weight='semibold' className='text-yellow-700'>
										{results.score.passivesPercentage.toFixed(1)}%
									</Text>
								</div>
								<div className='bg-gray-200 rounded-full h-2'>
									<div
										className='bg-yellow-500 h-2 rounded-full transition-all'
										style={{ width: `${results.score.passivesPercentage}%` }}
									/>
								</div>
								<Text variant='xsmall' className='text-gray-600 mt-1'>
									{results.score.passivesCount} responses
								</Text>
							</div>
							<div>
								<div className='flex justify-between items-center mb-1'>
									<Text variant='xsmall' className='text-red-700'>
										Detractors (0-6)
									</Text>
									<Text variant='xsmall' weight='semibold' className='text-red-700'>
										{results.score.detractorsPercentage.toFixed(1)}%
									</Text>
								</div>
								<div className='bg-gray-200 rounded-full h-2'>
									<div
										className='bg-red-500 h-2 rounded-full transition-all'
										style={{ width: `${results.score.detractorsPercentage}%` }}
									/>
								</div>
								<Text variant='xsmall' className='text-gray-600 mt-1'>
									{results.score.detractorsCount} responses
								</Text>
							</div>
						</div>

						{/* Completion progress bar */}
						<div className='mt-4'>
							<div className='flex justify-between mb-1'>
								<Text variant='xsmall' className='text-neutral-600'>
									Response Rate
								</Text>
								<Text variant='xsmall' weight='semibold' className='text-neutral-700'>
									{results.score.responseRate.toFixed(1)}%
								</Text>
							</div>
							<div className='w-full bg-gray-200 rounded-full h-2'>
								<div
									className='bg-green-500 h-2 rounded-full transition-all'
									style={{ width: `${Math.min(results.score.responseRate, 100)}%` }}
								/>
							</div>
						</div>
					</div>
				)}

				{/* Actions */}
				<div className='space-y-3'>
					<Text variant='medium' weight='semibold'>
						Actions
					</Text>

					{enpsConfig?.status === 'draft' && (
						<div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
							<Text variant='small' className='text-blue-900'>
								Activate this survey to enable scheduled sending based on the cadence.
							</Text>
							<Button variant='primary' onClick={handleActivate} className='mt-3'>
								Activate Survey
							</Button>
						</div>
					)}

					{enpsConfig?.status === 'active' && (
						<>
							<div className='bg-green-50 border border-green-200 rounded-lg p-4'>
								<Text variant='small' className='text-green-900'>
									Survey is active and will be sent automatically based on the schedule.
								</Text>
								<Button variant='secondary' onClick={handlePause} className='mt-3'>
									Pause Survey
								</Button>
							</div>

							<div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
								<Text variant='small' className='text-yellow-900'>
									{activeExecution
										? 'There is already an active execution. Please close it before launching a new one.'
										: 'Launch an immediate survey to all configured participants.'}
								</Text>
								<Button variant='primary' onClick={handleLaunch} className='mt-3' disabled={!!activeExecution}>
									Launch Survey Now
								</Button>
							</div>
						</>
					)}

					{enpsConfig?.status === 'paused' && (
						<div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
							<Text variant='small' className='text-yellow-900'>
								Survey is paused. Activate to resume scheduled sending.
							</Text>
							<Button variant='primary' onClick={handleActivate} className='mt-3'>
								Resume Survey
							</Button>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
